import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
const RESOLUTIONS = '/api/v1/admin/payment-resolutions';
const RESOLUTION_KEY = ['og', 'payment-resolutions'];
const SETTINGS = '/api/v1/admin/admin-ui-settings';
const SETTINGS_KEY = ['og', 'admin-ui-settings'];
export function buildPaymentResolutionSearch(filters) {
    const params = new URLSearchParams();
    const optional = [
        ['resolution_id', filters.resolution_id],
        ['order_id', filters.order_id],
        ['transaction_id', filters.transaction_id],
        ['status', filters.status],
    ];
    for (const [key, value] of optional) {
        const normalized = value?.trim();
        if (normalized)
            params.set(key, normalized);
    }
    params.set('limit', String(filters.limit));
    params.set('offset', String(filters.offset));
    return params.toString();
}
export function usePaymentResolutions(filters) {
    const search = buildPaymentResolutionSearch(filters);
    return useQuery({
        queryKey: [...RESOLUTION_KEY, 'list', filters],
        queryFn: () => apiOg('payments', `${RESOLUTIONS}?${search}`),
        placeholderData: keepPreviousData,
    });
}
export function usePaymentResolution(resolutionId, enabled = true) {
    return useQuery({
        queryKey: [...RESOLUTION_KEY, 'detail', resolutionId],
        queryFn: () => apiOg('payments', `${RESOLUTIONS}/${encodeURIComponent(resolutionId)}`),
        enabled: enabled && resolutionId !== '',
    });
}
function invalidateResolution(queryClient, resolutionId) {
    void queryClient.invalidateQueries({ queryKey: RESOLUTION_KEY });
    void queryClient.invalidateQueries({
        queryKey: [...RESOLUTION_KEY, 'detail', resolutionId],
    });
}
export function useRetryPaymentResolution() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ resolutionId, comment }) => apiOg('payments', `${RESOLUTIONS}/${encodeURIComponent(resolutionId)}/retry`, { method: 'POST', body: { comment: comment.trim() } }),
        onSettled: (_data, _error, variables) => invalidateResolution(queryClient, variables.resolutionId),
    });
}
export function useRecordPaymentRefund() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ resolutionId, comment, refund_reference }) => apiOg('payments', `${RESOLUTIONS}/${encodeURIComponent(resolutionId)}/record-refund`, {
            method: 'POST',
            body: {
                comment: comment.trim(),
                refund_reference: typeof refund_reference === 'string'
                    ? refund_reference.trim() || null
                    : refund_reference,
            },
        }),
        onSettled: (_data, _error, variables) => invalidateResolution(queryClient, variables.resolutionId),
    });
}
export function useAdminUiSettings(enabled = true) {
    return useQuery({
        queryKey: SETTINGS_KEY,
        queryFn: () => apiOg('payments', SETTINGS),
        enabled,
    });
}
export function useUpdateAdminUiSettings() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patch) => apiOg('payments', SETTINGS, { method: 'PATCH', body: patch }),
        onSuccess: (settings) => queryClient.setQueryData(SETTINGS_KEY, settings),
    });
}
